import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import apiClient from '@/lib/api'
import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { StructureCard } from '@/components/structures/StructureCard'
import { StructureFormModal } from '@/components/structures/StructureFormModal'
import type { Structure } from '@/components/structures/StructureCard'
import type { StructureFormData } from '@/components/structures/StructureFormModal'
import {
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  PlusIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

interface StructureRequest {
  identifierCode: string
  name: string
  type: string
  region: string
  district: string
  latitude?: number | null
  longitude?: number | null
}

async function fetchStructures(): Promise<Structure[]> {
  const res = await apiClient.get<Structure[]>('/api/v1/admin/structures')
  return res.data
}

async function createStructure(body: StructureRequest): Promise<Structure> {
  const res = await apiClient.post<Structure>('/api/v1/admin/structures', body)
  return res.data
}

async function updateStructure(id: string, body: StructureRequest): Promise<Structure> {
  const res = await apiClient.put<Structure>(`/api/v1/admin/structures/${id}`, body)
  return res.data
}

async function deleteStructure(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/admin/structures/${id}`)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function StructuresPage() {
  const queryClient = useQueryClient()

  // State
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingStructure, setEditingStructure] = useState<Structure | null>(null)
  const [deletingStructure, setDeletingStructure] = useState<Structure | null>(null)

  // Query
  const {
    data: structures = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['structures'],
    queryFn: fetchStructures,
  })

  // Mutations
  const createMutation = useMutation({
    mutationFn: (body: StructureRequest) => createStructure(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['structures'] })
      toast.success('Structure créée avec succès')
      handleCloseModal()
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Erreur lors de la création')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: StructureRequest }) => updateStructure(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['structures'] })
      toast.success('Structure mise à jour')
      handleCloseModal()
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Erreur lors de la mise à jour')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteStructure(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['structures'] })
      toast.success('Structure supprimée')
      setDeletingStructure(null)
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Erreur lors de la suppression')
    },
  })

  // Filtered list
  const filtered = useMemo(() => {
    if (!search.trim()) return structures
    const q = search.toLowerCase()
    return structures.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.identifierCode.toLowerCase().includes(q) ||
        s.type.toLowerCase().includes(q) ||
        (s.district ?? '').toLowerCase().includes(q)
    )
  }, [structures, search])

  // Handlers
  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
    setEditingStructure(null)
  }, [])

  const handleOpenCreate = useCallback(() => {
    setEditingStructure(null)
    setModalOpen(true)
  }, [])

  const handleEdit = useCallback((structure: Structure) => {
    setEditingStructure(structure)
    setModalOpen(true)
  }, [])

  const handleDelete = useCallback((structure: Structure) => {
    setDeletingStructure(structure)
  }, [])

  const handleFormSubmit = useCallback(
    (data: StructureFormData) => {
      const body: StructureRequest = {
        name: data.name.trim(),
        identifierCode: data.identifierCode.trim(),
        type: data.type,
        region: data.region.trim(),
        district: data.district.trim(),
        latitude: data.latitude ? parseFloat(data.latitude) : null,
        longitude: data.longitude ? parseFloat(data.longitude) : null,
      }

      if (editingStructure) {
        updateMutation.mutate({ id: editingStructure.id, body })
      } else {
        createMutation.mutate(body)
      }
    },
    [editingStructure, createMutation, updateMutation]
  )

  const handleConfirmDelete = useCallback(() => {
    if (deletingStructure) {
      deleteMutation.mutate(deletingStructure.id)
    }
  }, [deletingStructure, deleteMutation])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-8 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Structures de santé</h1>
          <p className="text-sm text-neutral-500 mt-0.5">
            Répertoire des structures sanitaires de Dakar
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!isLoading && (
            <Badge variant="info" size="sm">
              {structures.length} structure{structures.length !== 1 ? 's' : ''}
            </Badge>
          )}
          <Button
            variant="primary"
            size="sm"
            leftIcon={<PlusIcon className="w-4 h-4" />}
            onClick={handleOpenCreate}
          >
            Ajouter
          </Button>
        </div>
      </div>

      {/* Search */}
      <Input
        placeholder="Rechercher par nom, code, type ou district..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
      />

      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Spinner size="lg" />
          <p className="text-sm text-neutral-500">Chargement des structures...</p>
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-error-50">
            <ExclamationTriangleIcon className="w-6 h-6 text-error-500" />
          </div>
          <p className="text-sm font-medium text-neutral-900">Erreur de chargement</p>
          <p className="text-sm text-neutral-500">
            {(error as { message?: string })?.message ?? 'Impossible de charger les structures'}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['structures'] })}
          >
            Réessayer
          </Button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-neutral-100">
            <BuildingOffice2Icon className="w-6 h-6 text-neutral-400" />
          </div>
          {search.trim() ? (
            <>
              <p className="text-sm font-medium text-neutral-900">Aucun résultat</p>
              <p className="text-sm text-neutral-500">
                Aucune structure ne correspond à "{search}"
              </p>
              <Button variant="outline" size="sm" onClick={() => setSearch('')}>
                Effacer la recherche
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-neutral-900">Aucune structure</p>
              <p className="text-sm text-neutral-500">
                Ajoutez votre première structure de santé
              </p>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<PlusIcon className="w-4 h-4" />}
                onClick={handleOpenCreate}
              >
                Ajouter une structure
              </Button>
            </>
          )}
        </div>
      )}

      {/* Structure cards */}
      {!isLoading && !isError && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((structure) => (
            <StructureCard
              key={structure.id}
              structure={structure}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      <StructureFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleFormSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        structure={editingStructure}
      />

      {/* Delete confirmation dialog */}
      {deletingStructure && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDeletingStructure(null)
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className={cn(
              'relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6',
              'space-y-4'
            )}
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-error-50 shrink-0">
                <ExclamationTriangleIcon className="w-5 h-5 text-error-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-neutral-900">
                  Supprimer la structure
                </h3>
                <p className="text-sm text-neutral-500 mt-0.5">
                  Voulez-vous vraiment désactiver "{deletingStructure.name}" ?
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeletingStructure(null)}
                disabled={deleteMutation.isPending}
              >
                Annuler
              </Button>
              <Button
                variant="destructive"
                size="sm"
                isLoading={deleteMutation.isPending}
                onClick={handleConfirmDelete}
              >
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
